import React, { useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { fetchUser } from '@/src/actions/admin/v2/adminUsersActions'
import { UserWithAdminInfo } from '@/src/reducers/admin/v2/allUsersReducerV2'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/src/features/OpenFeature'
import { getPlaidOnlyFinancialAccountsByUserId } from '@/src/selectors/financeSelectors'
import { useReselector } from '@/src/utils/sharedHooks'
import { FinancialAccountWithAdminInfo } from '@/src/reducers/admin/financialAccountsReducer'
import { BookkeepingReport } from '@/src/reducers/finances/bookkeepingReportsReducer'
import { DATE_FORMATS_LUXON } from '@/src/utils/dateHelpers'

export type TransactionRowModel = {
  id: number
  credit: string | null
  debit: string | null
  description: string
  manualUpload: boolean
  matched: boolean
  reconciliationId: number
  date: string
}

export type ReactClickSignature = (
  _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null
) => void

export interface TransactionCollection {
  [key: string]: TransactionRowModel
}

export const CUBK_INSTITUTIONS = [
  'ins_3', // Chase
  'ins_56', // Chase
  'ins_1', // Bank of America
  'ins_127989', // Bank of America
  'ins_127296', // BlueVine
  'ins_10', // American Express
  'ins_117228', // Relay Financial
]

export const CUBK_INSTITUTIONS_EXPANDED = [
  'ins_128026', // Capital One
  'ins_13', // PNC
  'ins_15', // Navy Federal Credit Union
  'ins_115585', // Boeing Employees Credit Union
  'ins_132102', // Boeing Employees Credit Union
  'ins_116594', // Novo
]

export const useCUBKInstitutionList = () => {
  const autoReconCUBKEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableAutoreconCUBK,
    false
  )
  const autoReconCUBKExpandedEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableAutoreconCUBKExpanded,
    false
  )
  const institutionList = useMemo(() => {
    if (autoReconCUBKExpandedEnabled) {
      return [...CUBK_INSTITUTIONS, ...CUBK_INSTITUTIONS_EXPANDED]
    }
    if (autoReconCUBKEnabled) {
      return CUBK_INSTITUTIONS
    }
    return []
  }, [autoReconCUBKExpandedEnabled, autoReconCUBKEnabled])

  return institutionList
}

export const useAutoReconCUBK = ({
  userId,
  account,
  bookkeepingReport,
}: {
  userId: number
  account?: FinancialAccountWithAdminInfo
  bookkeepingReport?: BookkeepingReport
}) => {
  const [user, setUser] = useState<UserWithAdminInfo>()
  const [loading, setLoading] = useState(true)

  const isEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableAutoreconCUBK,
    false
  )

  const isAutoReconCUBKPhase3Enabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableAutoreconCUBK3,
    false
  )

  const plaidAccounts = useReselector(
    getPlaidOnlyFinancialAccountsByUserId,
    userId
  )

  const institutionList = useCUBKInstitutionList()

  useEffect(() => {
    const fetchAdminUser = async () => {
      const result = await fetchUser({
        id: userId,
        select: ['catchUpBookkeepingStatus'],
      })
      setUser(result)
      setLoading(false)
    }
    fetchAdminUser()
  }, [userId])

  const isEligible = useMemo(() => {
    if (!isEnabled) return false
    if (!user) return false
    const { catchUpBookkeepingStatus } = user
    if (!catchUpBookkeepingStatus) return false
    if (plaidAccounts.length === 0) return false

    // If Phase 3 is enabled, allow all institutions
    if (isAutoReconCUBKPhase3Enabled) {
      return catchUpBookkeepingStatus.status === 'incomplete'
    }

    // Otherwise, use the original institution-based logic
    if (account && !institutionList.includes(account.plaidInstitutionId || ''))
      return false

    return (
      catchUpBookkeepingStatus.status === 'incomplete' &&
      institutionList.some((institution) =>
        plaidAccounts.some(
          (account) => account.plaidInstitutionId === institution
        )
      )
    )
  }, [
    user,
    isEnabled,
    plaidAccounts,
    account,
    institutionList,
    isAutoReconCUBKPhase3Enabled,
  ])

  const autoReconPeriod = useMemo(() => {
    if (!bookkeepingReport || !isEligible) return null
    const { date } = bookkeepingReport
    const startDate = DateTime.fromFormat(date, DATE_FORMATS_LUXON.YEAR_MONTH)
      .plus({ month: 1 })
      .startOf('month')
    const previousMonthDate = DateTime.now()
      .minus({ month: 1 })
      .startOf('month')
    if (startDate > previousMonthDate) return null
    return [startDate, previousMonthDate]
  }, [bookkeepingReport, isEligible])

  const autoReconPeriodFormattedShort = useMemo(() => {
    if (!autoReconPeriod) return null
    const [startDate, previousMonthDate] = autoReconPeriod
    if (startDate.equals(previousMonthDate)) return startDate.monthShort
    return `${startDate.monthShort} - ${previousMonthDate.monthShort}`
  }, [autoReconPeriod])

  const autoReconPeriodFormattedLong = useMemo(() => {
    if (!autoReconPeriod) return null
    const [startDate, previousMonthDate] = autoReconPeriod
    if (startDate.equals(previousMonthDate))
      return `${startDate.monthLong} ${startDate.year}`
    return `${startDate.monthLong} - ${previousMonthDate.monthLong} ${previousMonthDate.year}`
  }, [autoReconPeriod])

  return {
    isEligible,
    autoReconPeriod,
    autoReconPeriodFormattedShort,
    autoReconPeriodFormattedLong,
    loading,
  }
}

export const isAfterAutoReconPeriod = (isoString: string) => {
  const previousMonth = DateTime.utc().minus({ month: 1 }).endOf('month')
  const reportDate = DateTime.fromISO(isoString, { zone: 'utc' })
  return reportDate > previousMonth
}
